import { useCallback, useState } from 'react';
import type { ChangeEvent, FC, ReactNode } from 'react';

import { Col } from '@opendoor/atoms/box';
import { useConnectedField } from '@opendoor/atoms/field';
import { Form } from '@opendoor/atoms/form';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';

import { AuthFormError } from '../AccountForm/AccountFormV2/primitives/AuthFormError';
import { OrDivider } from '../AccountForm/AccountFormV2/primitives/OrDivider';
import { PasswordInput } from '../AccountForm/AccountFormV2/primitives/PasswordInput';
import { useAnalyticsPrefix, useAuthAuthenticated } from '../AuthContext';

/** @public */
export interface ReauthFormAdditionalAuthContext {
  verifyLogin: ReturnType<typeof useAuthAuthenticated>['verifyLogin'];
  onSuccess: () => void;
  onFailure: () => void;
}

export interface ReauthFormProps {
  onSuccess: () => void;
  title?: string;
  resetPasswordHref?: string;
  /**
   * Overrides the composed `APP-reauth` name stem for surfaces that need to be
   * told apart in analytics (cosmos's account gate passes
   * `cosmos-account-reauth`). Omit it and the stem comes from the auth
   * context's `analyticsPrefix`.
   */
  analyticsPrefix?: string;
  renderAdditionalAuth?: (context: ReauthFormAdditionalAuthContext) => ReactNode;
}

const ANALYTICS_COMPONENT = 'reauth';

const DEFAULT_HEADING = 'Confirm your password to continue';
const SUBHEADING =
  'For your security, confirm your password before making changes to your account.';
const LOGIN_FAILED_MESSAGE =
  'Incorrect email or password. Use the link below to reset your password';

const reauthSchema = z.object({ password: z.string().min(1, 'Enter your password') });

/** Wires the atoms `PasswordInput` (with its visibility toggle) to the TanStack
 *  form field. Errors surface in the shared `AuthFormError` region, so the
 *  control shows only the ring + `aria-invalid`, never an inline message. */
const PasswordField: FC<{ analyticsName: string }> = ({ analyticsName }) => {
  const f = useConnectedField('password');
  return (
    <Col className="gap-1.5">
      <Text as="label" htmlFor={f.id} className="text-label-sm text-od-content-primary">
        Password
      </Text>
      <PasswordInput
        id={f.id}
        name={f.name}
        value={typeof f.value === 'string' ? f.value : ''}
        // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- PasswordInput isn't memoized, so the fresh handler identity is harmless (and useConnectedField returns a fresh setValue each render, so useCallback couldn't stabilize it anyway)
        onChange={(event: ChangeEvent<HTMLInputElement>) => f.setValue(event.target.value)}
        onBlur={f.onBlur}
        error={f.hasError}
        aria-invalid={f.hasError || undefined}
        analyticsName={analyticsName}
        autoComplete="current-password"
      />
    </Col>
  );
};

/** UI for the user to reauthenticate. Commonly used before allowing the user to view sensitive data. */
export const ReauthForm: FC<ReauthFormProps> = ({
  onSuccess,
  title,
  resetPasswordHref = '/reset-password',
  analyticsPrefix,
  renderAdditionalAuth,
}) => {
  const { user, verifyLogin } = useAuthAuthenticated();
  const appPrefix = useAnalyticsPrefix();
  // `||`, not `??`: an empty override would otherwise name every control `-…`
  // and reach the DOM as `data-testid="-error"`, a blank-looking selector.
  const analyticsStem = analyticsPrefix || `${appPrefix}-${ANALYTICS_COMPONENT}`;
  const [loginFailed, setLoginFailed] = useState(false);

  const reportLoginFailure = useCallback(() => {
    setLoginFailed(true);
  }, []);

  const form = useForm({
    defaultValues: { password: '' },
    validators: { onChange: reauthSchema },
    onSubmit: async ({ value }) => {
      setLoginFailed(false);
      try {
        // A failed re-auth must land in the form's error region, not escape
        // `handleSubmit` as an unhandled rejection — so catch, don't rethrow.
        const result = await verifyLogin({ email: user.email, password: value.password });
        if (result.success) {
          onSuccess();
        } else {
          reportLoginFailure();
        }
      } catch {
        reportLoginFailure();
      }
    },
  });

  return (
    <Col className="w-full gap-6">
      <Col className="gap-2">
        <Text as="h2" className="text-subheader-md text-od-content-primary">
          {title || DEFAULT_HEADING}
        </Text>
        <Text as="p" className="text-body-sm text-od-content-secondary">
          {SUBHEADING}
        </Text>
      </Col>
      <Form.Root
        form={form}
        noValidate
        scrollToFirstError={false}
        className="flex w-full flex-col gap-4"
      >
        <AuthFormError
          serverErrors={loginFailed ? LOGIN_FAILED_MESSAGE : null}
          data-testid={`${analyticsStem}-error`}
        />
        <Col className="gap-2">
          <PasswordField analyticsName={`${analyticsStem}-password`} />
          <Link
            href={resetPasswordHref}
            target="_blank"
            rel="noopener noreferrer"
            analyticsName={`${analyticsStem}-forgot-password`}
            aria-label="Opens the forgot password page"
            className="text-body-sm"
          >
            Forgot your password?
          </Link>
        </Col>
        <Form.SubmitButton
          className="w-full"
          aria-label="Confirm and submit password"
          analyticsName={`${analyticsStem}-submit`}
        >
          Confirm
        </Form.SubmitButton>
      </Form.Root>
      {renderAdditionalAuth ? (
        <Col className="w-full gap-6">
          <OrDivider />
          {renderAdditionalAuth({ verifyLogin, onSuccess, onFailure: reportLoginFailure })}
        </Col>
      ) : null}
    </Col>
  );
};
